import { axiosClient } from '@/features'

type InvoiceStatus = 'OPENED' | 'IN_PROGRESS' | 'PAID' | 'ERROR' | 'CLOSED' | 'ABORTED'
import { Card, CardDetails, DelegateResponse } from '@/features/Cards/cards.types'
import { ApiRoutes } from '@/routes'
import { getUrl } from '@/shared/helpers/url'
import { TariffPlan } from '../store/tariffs/tariffs.store'

export const cardsService = {
  getUserCards: async (): Promise<Card[]> => {
    const req = await axiosClient.get(ApiRoutes.GET_USER_CARDS)
    return req.data
  },

  canIssue: async () => {
    const req = await axiosClient.get(ApiRoutes.KYC_CAN_ISSUE)
    return req.data
  },

  issueCard: async (tokenAddress: string, tokenMint: string, publicKey?: string) => {
    const req = await axiosClient.post(ApiRoutes.CARD_ISSUE, {
      tokenAddress, tokenMint, jwePublicKeyBase64: publicKey,
    })
    return req.data
  },

  freezeCard: async (cardId: string) => {
    const req = await axiosClient.put(getUrl(ApiRoutes.CARD_FREEZE, { id: cardId }))
    return req.data
  },

  unFreezeCard: async (cardId: string) => {
    const req = await axiosClient.put(getUrl(ApiRoutes.CARD_UNFREEZE, { id: cardId }))
    return req.data
  },

  renameCard: async (cardId: string, name: string) => {
    const req = await axiosClient.put(getUrl(ApiRoutes.SET_CARD_NAME, { id: cardId }), {
      name,
    })
    return req.data
  },

  blockCard: async (cardId: string, last4: string) => {
    const req = await axiosClient.put(getUrl(ApiRoutes.BLOCK_CARD, { id: cardId }), {
      last4PanDigits: last4,
    })
    return req.data
  },

  getPinEncryptionPublicKey: async (): Promise<{ keyBase64: string }> => {
    const req = await axiosClient.get<{ keyBase64: string }>(ApiRoutes.CARD_PIN_ENCRYPTION_KEY)
    return req.data
  },

  setPinCard: async (
    cardId: string,
    encryptedPin: string,
    encodedIv: string,
    sessionIdBase64: string,
  ) => {
    const url = `${getUrl(ApiRoutes.SET_CARD_PIN, { id: cardId })}?sessionIdBase64=${encodeURIComponent(sessionIdBase64)}`
    const req = await axiosClient.put(url, {
      iv: encodedIv,
      data: encryptedPin,
    })
    return req.data
  },

  createInvoices: async (mint: string): Promise<{invoiceId: string}> => {
    const req = await axiosClient.post(ApiRoutes.CARDS_CREATE_INVOICE, { mint })
    return req.data
  },

  checkInvoice: async (invoiceId: string): Promise<{ invoiceId: string; status: InvoiceStatus }> => {
    const req = await axiosClient.get(getUrl(ApiRoutes.CARDS_CHECK_INVOICE_STATUS, { id: invoiceId }))
    return req.data
  },

  invoiceBilling: async (invoiceId: string, fromWalletAddress: string, fromTokenAddress: string): Promise<DelegateResponse> => {
    const req = await axiosClient.post(getUrl(ApiRoutes.CARDS_BILLING_INIT, { id: invoiceId }), {
      fromWalletAddress,
      fromTokenAddress,
    })
    return req.data
  },

  getTariffs: async () => {
    const req = await axiosClient.get<{currentInstanceId: number; instances: TariffPlan[]}>(ApiRoutes.TARIFFS)
    return req.data
  },

  payTariff: async (mint: string, id: number) => {
    const req = await axiosClient.post(ApiRoutes.PAY_TARIFF, {
      tariffPlanInstanceId: id,
      mint,
    })
    return req.data
  },

  changeAccount: async (cardId: string, tokenAddress: string, tokenMint: string) => {
    const req = await axiosClient.put(getUrl(ApiRoutes.CARDS_CHANGE_ACCOUNT, { id: cardId }), {
      tokenAddress,
      tokenMint,
    })
    return req.data
  }
}
